import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import apiAxiosInstance from '../../utils/axiosInstance';

interface Standard {
  _id: string;
  name: string;
  description: string;
  state: string;
}

interface DetailedStandard {
  _id: string;
  strand: string;
  standard: string;
  description: string;
  short_description: string;
  gradeLevel: string;
  subject: string;
}

interface GroupedStandards {
  [gradeLevel: string]: DetailedStandard[];
}

const StandardsVisualization: React.FC = () => {
  const [mainStandards, setMainStandards] = useState<Standard[]>([]);
  const [selectedMainStandard, setSelectedMainStandard] = useState<string>('');
  const [selectedSubject, setSelectedSubject] = useState<string>('');
  const [groupedStandards, setGroupedStandards] = useState<GroupedStandards>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [subjects, setSubjects] = useState<string[]>([]);

  // Grade order for display - includes numeric grades and high school course names
  const gradeOrder = ['K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', 'Reading', 'Speech'];

  // Fetch main standards
  useEffect(() => {
    const fetchMainStandards = async () => {
      try {
        const response = await apiAxiosInstance.get('/api/standards');
        setMainStandards(response.data || []);
      } catch (err: any) {
        console.error('Error fetching main standards:', err);
        setError('Failed to load main standards');
      }
    };
    fetchMainStandards();
  }, []);

  // Fetch detailed standards when main standard and subject are selected
  useEffect(() => {
    const fetchDetailedStandards = async () => {
      if (!selectedMainStandard || !selectedSubject) {
        setGroupedStandards({});
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const response = await apiAxiosInstance.get('/api/detailedstandards/by-standard', {
          params: {
            teachingStandards: selectedMainStandard,
            subject: selectedSubject,
          },
        });
        setGroupedStandards(response.data || {});
      } catch (err: any) {
        console.error('Error fetching detailed standards:', err);
        setError('Failed to load detailed standards');
        setGroupedStandards({});
      } finally {
        setLoading(false);
      }
    };

    fetchDetailedStandards();
  }, [selectedMainStandard, selectedSubject]);

  // Fetch available subjects when main standard is selected
  useEffect(() => {
    const fetchSubjects = async () => {
      if (!selectedMainStandard) {
        setSubjects([]);
        setSelectedSubject('');
        return;
      }

      setLoading(true);
      try {
        // Fetch all detailed standards for this main standard to get available subjects
        const response = await apiAxiosInstance.get('/api/detailedstandards', {
          params: {
            teachingStandards: selectedMainStandard,
          },
        });
        
        const uniqueSubjects = new Set<string>();
        (response.data || []).forEach((std: DetailedStandard) => {
          if (std.subject) {
            uniqueSubjects.add(std.subject);
          }
        });
        
        const subjectsList = Array.from(uniqueSubjects).sort();
        setSubjects(subjectsList);
        
        // Auto-select subject if only one is available
        if (subjectsList.length === 1) {
          setSelectedSubject(subjectsList[0]);
        } else if (subjectsList.length > 0 && !subjectsList.includes(selectedSubject)) {
          // Reset subject if current selection is not available
          setSelectedSubject('');
        }
      } catch (err: any) {
        console.error('Error fetching subjects:', err);
        setError('Failed to load available subjects');
      } finally {
        setLoading(false);
      }
    };

    fetchSubjects();
  }, [selectedMainStandard]);

  const handleMainStandardChange = (event: any) => {
    setSelectedMainStandard(event.target.value);
    setSelectedSubject(''); // Reset subject when main standard changes
  };

  const handleSubjectChange = (event: any) => {
    setSelectedSubject(event.target.value);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Standards Visualization
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
        <FormControl sx={{ minWidth: 250 }}>
          <InputLabel>Main Standard</InputLabel>
          <Select
            value={selectedMainStandard}
            onChange={handleMainStandardChange}
            label="Main Standard"
          >
            {mainStandards.map((standard) => (
              <MenuItem key={standard._id} value={standard._id}>
                {standard.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 250 }} disabled={!selectedMainStandard || loading}>
          <InputLabel>Subject</InputLabel>
          <Select
            value={selectedSubject}
            onChange={handleSubjectChange}
            label="Subject"
          >
            {subjects.map((subject) => (
              <MenuItem key={subject} value={subject}>
                {subject}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && selectedMainStandard && selectedSubject && Object.keys(groupedStandards).length === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          No standards found for the selected main standard and subject.
        </Alert>
      )}

      {!loading && Object.keys(groupedStandards).length > 0 && (
        <Box>
          {gradeOrder
            .filter(grade => groupedStandards[grade] && groupedStandards[grade].length > 0)
            .map((grade) => {
              // Check if grade is a high school course name (not numeric)
              const isHighSchoolCourse = !/^[0-9K]+$/.test(grade);
              const displayLabel = isHighSchoolCourse ? grade : `Grade ${grade}`;
              
              return (
              <Paper key={grade} sx={{ mb: 3, p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h5" component="h2">
                    {displayLabel}
                  </Typography>
                  <Chip
                    label={`${groupedStandards[grade].length} standards`}
                    size="small"
                    sx={{ ml: 2 }}
                  />
                </Box>
                <Divider sx={{ mb: 2 }} />
                <List>
                  {groupedStandards[grade].map((standard) => (
                    <ListItem key={standard._id} sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, width: '100%' }}>
                        <Chip
                          label={standard.standard}
                          size="small"
                          color="primary"
                          sx={{ mr: 1 }}
                        />
                        <Chip
                          label={standard.strand}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                      <ListItemText
                        primary={standard.description}
                        secondary={standard.short_description !== standard.description ? standard.short_description : undefined}
                        sx={{ mt: 0.5 }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
              );
            })}
          {/* Show any additional grade levels not in gradeOrder (fallback) */}
          {Object.keys(groupedStandards)
            .filter(grade => !gradeOrder.includes(grade) && groupedStandards[grade].length > 0)
            .map((grade) => (
              <Paper key={grade} sx={{ mb: 3, p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h5" component="h2">
                    {grade}
                  </Typography>
                  <Chip
                    label={`${groupedStandards[grade].length} standards`}
                    size="small"
                    sx={{ ml: 2 }}
                  />
                </Box>
                <Divider sx={{ mb: 2 }} />
                <List>
                  {groupedStandards[grade].map((standard) => (
                    <ListItem key={standard._id} sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, width: '100%' }}>
                        <Chip
                          label={standard.standard}
                          size="small"
                          color="primary"
                          sx={{ mr: 1 }}
                        />
                        <Chip
                          label={standard.strand}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                      <ListItemText
                        primary={standard.description}
                        secondary={standard.short_description !== standard.description ? standard.short_description : undefined}
                        sx={{ mt: 0.5 }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            ))}
        </Box>
      )}
    </Container>
  );
};

export default StandardsVisualization;

